import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PartnerSearchByBrResponse, ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { useGetServiceAgreementDetails } from '../../../../../hooks/queries/privateBanking';
import { isSafeDepositBoxServiceAgreementProperties } from '../../../productSpecificProperties';
import {
  useGetSafeDepositBoxServiceRoleOptions,
  useSaveSafeDepositBoxServiceRoles,
} from '../../../../../hooks/queries/privateBankingServiceRoles';
import { useErrorModal } from '../../../../../hooks/useErrorModal';
import { closeModal } from '../../../../../reducers/modalReducer';
import { setSelectedPBProduct } from '../../../../../reducers/privateBankingReducer';
import { showToast } from '../../../../../reducers/toastReducer';
import { filterVisibleServiceRoles } from '../../../../../utils/serviceRoleStatusUtils';
import { getErrorDetailsForCause } from '../../utils/saveUtils';
import type { SaveBlocker, SaveState } from '../../utils/saveGate';
import {
  buildPartnerFromSafeDepositBoxDraft,
  buildSafeDepositBoxDraftFromPartner,
  buildSafeDepositBoxDraftFromServiceRole,
  buildSafeDepositBoxServiceRolePayload,
  getSafeDepositBoxServiceRoleError,
  getSafeDepositBoxServiceRoleErrors,
  hasCompleteSafeDepositBoxServiceRoles,
  hasValidSafeDepositBoxSafeRole,
  haveSameSafeDepositBoxServiceRoles,
  isEditableServiceRoleRow,
  SAFE_HOLDER_API_VALUE,
  SafeDepositBoxServiceRoleDraft,
  SafeDepositBoxServiceRoleError,
} from '../utils/safeDepositBoxServiceRoleDraft';
import {
  buildSafeDepositBoxDetailsDraft,
  buildSafeDepositBoxDetailsPayload,
  getSafeDepositBoxDetailsErrors,
  haveSameSafeDepositBoxDetails,
  isSafeDepositBoxDetailsComplete,
  NO_SAFE_DEPOSIT_BOX_DETAILS_ERRORS,
  SafeDepositBoxDetailsDraft,
  SafeDepositBoxDetailsField,
} from '../utils/safeDepositBoxDetailsDraft';
import { areSafeDepositBoxDetailsLocked, isTerminalSafeDepositBoxStatus } from '../utils/safeDepositBoxStatus';
import { Active } from '../../../../../types';

type Props = {
  serviceAgreement: ServiceAgreementV2;
  onSaveStateChange: (state: SaveState) => void;
};

type DraftState = {
  key: string;
  rows: SafeDepositBoxServiceRoleDraft[];
  details: SafeDepositBoxDetailsDraft;
  touchedDetails: ReadonlySet<SafeDepositBoxDetailsField>;
  touchedRows: ReadonlySet<string>;
};

const NO_TOUCHED_DETAILS: ReadonlySet<SafeDepositBoxDetailsField> = new Set();
const NO_TOUCHED_ROWS: ReadonlySet<string> = new Set();
const NO_SERVICE_ROLE_ERRORS: ReadonlyMap<string, SafeDepositBoxServiceRoleError> = new Map();

export const useSafeDepositBoxServiceRoleSection = ({ serviceAgreement, onSaveStateChange }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showErrorModal } = useErrorModal();
  const serviceAgreementUid = serviceAgreement.serviceAgreementId;
  const [draftState, setDraftState] = useState<DraftState | null>(null);

  const detailsQuery = useGetServiceAgreementDetails(serviceAgreementUid);
  const optionsQuery = useGetSafeDepositBoxServiceRoleOptions(serviceAgreementUid);
  const properties = detailsQuery.data?.productSpecificProperties;
  const canConfigureSafeDepositBoxDetails =
    (isSafeDepositBoxServiceAgreementProperties(properties) && properties.canEditSafeNumber) ?? false;
  const { mutateAsync: saveServiceRoles, isPending: isSaving } = useSaveSafeDepositBoxServiceRoles();

  const visibleServiceRoles = useMemo(
    () => filterVisibleServiceRoles(detailsQuery.data?.serviceRoles).filter((role) => role.status !== 'INACTIVE'),
    [detailsQuery.data?.serviceRoles],
  );
  const hasInvalidSafeRole =
    detailsQuery.isSuccess && visibleServiceRoles.some((role) => !hasValidSafeDepositBoxSafeRole(role));
  const invalidSafeRoleError = useMemo(
    () =>
      hasInvalidSafeRole ? new Error('Safe Deposit Box service-role response is missing the required safeRole') : null,
    [hasInvalidSafeRole],
  );
  const initialRows = useMemo(
    () =>
      visibleServiceRoles
        .map(buildSafeDepositBoxDraftFromServiceRole)
        .filter((row): row is SafeDepositBoxServiceRoleDraft => !!row),
    [visibleServiceRoles],
  );
  const initialDetails = useMemo(
    () => buildSafeDepositBoxDetailsDraft(detailsQuery.data?.productSpecificProperties),
    [detailsQuery.data?.productSpecificProperties],
  );
  const draftKey = serviceAgreementUid ?? 'missing-service-agreement';
  const isCurrentDraft = draftState?.key === draftKey;
  const rows = isCurrentDraft ? draftState.rows : initialRows;
  const details = isCurrentDraft ? draftState.details : initialDetails;
  const touchedDetails = isCurrentDraft ? draftState.touchedDetails : NO_TOUCHED_DETAILS;
  const touchedRows = isCurrentDraft ? draftState.touchedRows : NO_TOUCHED_ROWS;
  const setRows = useCallback(
    (
      update: (current: SafeDepositBoxServiceRoleDraft[]) => SafeDepositBoxServiceRoleDraft[],
      touchedRowKey?: string,
    ) => {
      setDraftState((current) => {
        const isSameDraft = current?.key === draftKey;
        const currentTouchedRows = isSameDraft ? current.touchedRows : NO_TOUCHED_ROWS;

        return {
          key: draftKey,
          rows: update(isSameDraft ? current.rows : initialRows),
          details: isSameDraft ? current.details : initialDetails,
          touchedDetails: isSameDraft ? current.touchedDetails : NO_TOUCHED_DETAILS,
          touchedRows: touchedRowKey ? new Set(currentTouchedRows).add(touchedRowKey) : currentTouchedRows,
        };
      });
    },
    [draftKey, initialDetails, initialRows],
  );
  const setDetail = useCallback(
    (changes: Partial<SafeDepositBoxDetailsDraft>) => {
      setDraftState((current) => {
        const isSameDraft = current?.key === draftKey;
        const touched = new Set(isSameDraft ? current.touchedDetails : NO_TOUCHED_DETAILS);
        (Object.keys(changes) as (keyof SafeDepositBoxDetailsDraft)[])
          .filter((field): field is SafeDepositBoxDetailsField => field !== 'keyDeposit')
          .forEach((field) => touched.add(field));

        return {
          key: draftKey,
          rows: isSameDraft ? current.rows : initialRows,
          details: { ...(isSameDraft ? current.details : initialDetails), ...changes },
          touchedDetails: touched,
          touchedRows: isSameDraft ? current.touchedRows : NO_TOUCHED_ROWS,
        };
      });
    },
    [draftKey, initialDetails, initialRows],
  );

  const safeRoleOptions = optionsQuery.data?.safeRoles ?? [];
  const safeLocationOptions = optionsQuery.data?.safeLocations ?? [];
  const safeSizeOptions = optionsQuery.data?.safeSizes ?? [];
  // The grid memoizes its column definitions off these, so every value in the chain down to
  // serviceRoleErrors has to stay referentially stable or the cells render against a stale map.
  const signatureAuthorityOptions = useMemo(
    () => optionsQuery.data?.signatureAuthorities ?? [],
    [optionsQuery.data?.signatureAuthorities],
  );
  const signatureAuthorityValues = useMemo(
    () => new Set(signatureAuthorityOptions.map((option) => option.value)),
    [signatureAuthorityOptions],
  );
  const isLoading = detailsQuery.isFetching || optionsQuery.isFetching;
  const isError = detailsQuery.isError || optionsQuery.isError || invalidSafeRoleError !== null;
  const isBusy = isLoading || isError || isSaving;
  const isReadOnly = isTerminalSafeDepositBoxStatus(serviceAgreement.agreementStatus);
  const detailsLocked = areSafeDepositBoxDetailsLocked(serviceAgreement);
  const rolesDisabled = isBusy || isReadOnly;
  // Key deposit is agreed with the client per safe rather than fixed by the safe itself, so it outlives the
  // lock on location, size and number — but not the product: once the box is closed the API refuses it too.
  // Once active, key deposit is CDS-only in both directions.
  const keyDepositEditBlocked =
    isSafeDepositBoxServiceAgreementProperties(properties) && properties.canEditKeyDeposit === false;
  const keyDepositDisabled = isBusy || isReadOnly || keyDepositEditBlocked;
  const canEditSafeDepositBoxLocation =
    !isSafeDepositBoxServiceAgreementProperties(properties) || properties.canEditSafeDepositBoxLocation !== false;
  const detailsReadOnly = isBusy || detailsLocked || !canEditSafeDepositBoxLocation;
  const safeNrReadOnly = detailsReadOnly || !canConfigureSafeDepositBoxDetails;

  const serviceRoleErrorOptions = useMemo(
    () => ({ signatureAuthorityValues, touchedRows }),
    [signatureAuthorityValues, touchedRows],
  );
  const hasValidRows = hasCompleteSafeDepositBoxServiceRoles(rows, serviceRoleErrorOptions);
  const serviceRoleErrors = useMemo(
    () => (isBusy ? NO_SERVICE_ROLE_ERRORS : getSafeDepositBoxServiceRoleErrors(rows, serviceRoleErrorOptions)),
    [isBusy, rows, serviceRoleErrorOptions],
  );
  const hasUniquePartners = new Set(rows.map((row) => row.partnerUid)).size === rows.length;
  const isConfigurationChanged = !haveSameSafeDepositBoxDetails(initialDetails, details);
  // Locked details cannot be completed through this modal any more, so an incomplete legacy box must
  // still be saveable for the sake of its key deposit. CDS users act as validators - completeness is
  // only enforced for them; RM/RMA may save partial location/size changes with the box still incomplete.
  const hasValidConfiguration =
    detailsLocked || !canConfigureSafeDepositBoxDetails || isSafeDepositBoxDetailsComplete(details);
  const isChanged = !haveSameSafeDepositBoxServiceRoles(initialRows, rows) || isConfigurationChanged;
  const initialSafeHolderPartnerUids = useMemo(
    () => new Set(initialRows.filter((row) => row.safeRole === SAFE_HOLDER_API_VALUE).map((row) => row.partnerUid)),
    [initialRows],
  );
  const remainingPartnerUids = useMemo(() => new Set(rows.map((row) => row.partnerUid)), [rows]);
  const requiresProductClosureConfirmation =
    serviceAgreement.agreementStatus === Active.ACTIVE &&
    [...initialSafeHolderPartnerUids].some((partnerUid) => !remainingPartnerUids.has(partnerUid));
  const showProductClosureWarning = requiresProductClosureConfirmation && !rolesDisabled;
  const showServiceRoleValidationWarning = !isReadOnly && serviceRoleErrors.size > 0;
  const hasUneditableIncompleteRows = rows.some(
    (row) => !isEditableServiceRoleRow(row) && getSafeDepositBoxServiceRoleError(row, serviceRoleErrorOptions) !== null,
  );
  const canSave = !isBusy && !isReadOnly && hasValidRows && hasUniquePartners && isChanged && hasValidConfiguration;
  // None of the three fields are mandatory for RM/RMA - only a CDS user validates completeness, so only a
  // CDS user should see the inline required/invalid errors.
  const configurationErrors =
    isLoading || detailsReadOnly || !canConfigureSafeDepositBoxDetails
      ? NO_SAFE_DEPOSIT_BOX_DETAILS_ERRORS
      : getSafeDepositBoxDetailsErrors(details, touchedDetails);
  const configurationTooltip = hasValidConfiguration
    ? null
    : t('pb.configureproductmodal.safedepositbox.save.cdsrequired.tooltip');
  const tooltip =
    !isLoading && !isError && configurationTooltip
      ? configurationTooltip
      : !isLoading && !isError && hasUneditableIncompleteRows
        ? t('pb.configureproductmodal.safedepositbox.save.roles.incomplete.tooltip')
        : !isLoading && !isError && (!hasValidRows || !hasUniquePartners)
          ? t('pb.configureproductmodal.safedepositbox.save.invalid.tooltip')
          : !isLoading && !isError && !isChanged
            ? t('pb.configureproductmodal.safedepositbox.save.unchanged.tooltip')
            : null;

  const selectedPartners = useMemo(() => rows.map(buildPartnerFromSafeDepositBoxDraft), [rows]);

  const handleSelectedPartnersChange = useCallback(
    (partners: PartnerSearchByBrResponse[]) => {
      setRows((current) => {
        const currentPartnerUids = new Set(current.map((row) => row.partnerUid));
        const additions = partners
          .filter((partner) => !!partner.techId && !currentPartnerUids.has(partner.techId))
          .map(buildSafeDepositBoxDraftFromPartner);
        return additions.length > 0 ? [...current, ...additions] : current;
      });
    },
    [setRows],
  );

  const updateRow = useCallback(
    (key: string, changes: Partial<SafeDepositBoxServiceRoleDraft>) => {
      setRows(
        (current) =>
          current.map((row) =>
            row.key === key && isEditableServiceRoleRow(row)
              ? {
                  ...row,
                  signatureAuthority: Object.hasOwn(changes, 'signatureAuthority')
                    ? changes.signatureAuthority
                    : row.signatureAuthority,
                }
              : row,
          ),
        key,
      );
    },
    [setRows],
  );

  const removeRow = useCallback(
    (key: string) => {
      setRows((current) => current.filter((row) => row.key !== key || !row.isRemovable));
    },
    [setRows],
  );

  useEffect(() => {
    const error = detailsQuery.error ?? invalidSafeRoleError;
    if (!error) return;
    showErrorModal(error, {
      title: t('pb.configureproductmodal.safedepositbox.load.serviceroles.error.title'),
      userErrorMessage: t('pb.configureproductmodal.safedepositbox.load.serviceroles.error.message'),
    });
  }, [detailsQuery.error, invalidSafeRoleError, showErrorModal, t]);

  useEffect(() => {
    if (!optionsQuery.isError) return;
    showErrorModal(optionsQuery.error, {
      title: t('pb.configureproductmodal.safedepositbox.load.options.error.title'),
      userErrorMessage: t('pb.configureproductmodal.safedepositbox.load.options.error.message'),
    });
  }, [optionsQuery.error, optionsQuery.isError, showErrorModal, t]);

  const save = useCallback(async () => {
    if (!canSave || !serviceAgreementUid) return false;

    try {
      const updatedAgreement = await saveServiceRoles({
        serviceAgreementUid,
        serviceRoles: buildSafeDepositBoxServiceRolePayload(rows),
        // Omitted when unchanged: the backend treats absent as "don't touch" and publishes an
        // agreement event on every change, so always sending it would emit spurious events.
        safeDepositBoxDetails: isConfigurationChanged ? buildSafeDepositBoxDetailsPayload(details) : undefined,
      });
      dispatch(setSelectedPBProduct(updatedAgreement));
      dispatch(closeModal());
      dispatch(
        showToast({
          type: 'success',
          position: 'top-center',
          titleText: requiresProductClosureConfirmation
            ? t('pb.deleteproductmodal.success.message')
            : t('pb.configureproductmodal.safedepositbox.save.success.message'),
          dismissible: true,
          autoDismissDuration: 5000,
        }),
      );
      return true;
    } catch (error) {
      const details = await getErrorDetailsForCause(error);
      showErrorModal(error, {
        title: t('pb.configureproductmodal.safedepositbox.save.error.title'),
        userErrorMessage: t('pb.configureproductmodal.safedepositbox.save.error.message'),
        details,
      });
      return false;
    }
  }, [
    canSave,
    details,
    dispatch,
    isConfigurationChanged,
    requiresProductClosureConfirmation,
    rows,
    saveServiceRoles,
    serviceAgreementUid,
    showErrorModal,
    t,
  ]);

  const blocker = useMemo<SaveBlocker>(() => ({ blocked: !canSave, reason: tooltip }), [canSave, tooltip]);

  useEffect(() => {
    onSaveStateChange({ blocker, isPending: isSaving, save, requiresProductClosureConfirmation });
  }, [blocker, isSaving, onSaveStateChange, requiresProductClosureConfirmation, save]);

  return {
    rows,
    safeRoleOptions,
    signatureAuthorityOptions,
    selectedPartners,
    handleSelectedPartnersChange,
    updateRow,
    removeRow,
    isLoading,
    rolesDisabled,
    keyDepositDisabled,
    detailsReadOnly,
    detailsLocked,
    safeNrReadOnly,
    canConfigureSafeDepositBoxDetails,
    showProductClosureWarning,
    showServiceRoleValidationWarning,
    serviceRoleErrors,
    details,
    setDetail,
    safeLocationOptions,
    safeSizeOptions,
    configurationErrors,
    canEditSafeDepositBoxLocation,
  };
};
